/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { NotApplicableTypes, QuestionStatuses, TagStatus } from '../../../constants/status';
import { getNaType, isUtrvAssuranceComplete, isUtrvAssuranceRejected } from '../../../utils/universalTrackerValue';
import { CurrentUserData } from '../../../reducers/current-user';
import { Document } from 'flexsearch';
import { BlueprintContributions, ScopeQuestionGroupOptionalValue, SurveySettings } from '../../../types/survey';
import { ScopeQuestionOptionalValue } from '../../../types/surveyScope';
import { SDG_CODES } from '../../../constants/utr';
import { InitiativePlain, Materiality } from '../../../types/initiative';
import { Tag } from '../../../types/metricGroup';
import { UtrvAssuranceStatus } from '../../../types/universalTrackerValue';
import { Bookmark } from '../../../types/bookmark';

export interface DisableUtrsProps {
  surveyGroups: ScopeQuestionGroupOptionalValue[];
  blueprint?: BlueprintContributions;
  materiality?: InitiativePlain['materiality'];
  filterByStatus?: string[];
  filterByDelegationStatus?: string[];
  filterByMateriality?: string[];
  filterByGoal?: string[];
  filterByStandard?: string[];
  filterByRegulatory?: string[];
  filterByFramework?: string[];
  filterByTag?: string[];
  filterByBookmarks?: boolean;
  searchText?: string;
  searchIndex?: Document<unknown>;
  user?: CurrentUserData;
  tags?: Tag[];
  bookmarks?: Bookmark['targetId'][];
}

export const disableByGoal = ({ surveyGroups, filterByGoal, blueprint }: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByGoal' | 'blueprint'>) => {

  if (!filterByGoal || filterByGoal.length === 0) {
    return [];
  }

  const goalCodes = filterByGoal.map((goal: string) => `sdg/${goal}`);

  const disabledUTRs: string[] = [];

  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      const utrCode = question.universalTracker.getCode();
      if (goalCodes.every((code) => !blueprint?.[utrCode]?.includes(code))) {
        disabledUTRs.push(question.universalTracker.getId());
      }
    })
  );
  return disabledUTRs;
}

export const disableByStatus = ({ surveyGroups, filterByStatus }: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByStatus'>) => {

  if (!filterByStatus || filterByStatus.length === 0) {
    return [];
  }

  const disabledUTRs: string[] = [];
  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      const isStatus = (filteredStatus: string) => {
        if (!question.utrv) {
          return false;
        }
        const aggregatedStatus = question.utrv.status;
        switch (filteredStatus) {
          case QuestionStatuses.NA:
            return getNaType(question.utrv) === NotApplicableTypes.na;
          case QuestionStatuses.NR:
            return getNaType(question.utrv) === NotApplicableTypes.nr;
          case QuestionStatuses.Assured:
            return isUtrvAssuranceComplete(question.utrv.assuranceStatus);
          case QuestionStatuses.Disputed:
            return isUtrvAssuranceRejected(question.utrv.assuranceStatus);
          case QuestionStatuses.Created:
          case QuestionStatuses.Updated:
          case QuestionStatuses.Rejected:
          case QuestionStatuses.Verified:
            return filteredStatus === aggregatedStatus;
          case QuestionStatuses.Public:
            return !question.utrv.isPrivate;
          case QuestionStatuses.Private:
            return question.utrv.isPrivate === true;
          case QuestionStatuses.SentToAssurer:
            return !!question.utrv.assuranceStatus;
          case QuestionStatuses.NotAssured:
            return !question.utrv.assuranceStatus;
          case QuestionStatuses.Restated:
            return question.utrv.assuranceStatus === UtrvAssuranceStatus.Restated;
          default:
            return false;
        }
      }
      if (filterByStatus.every((status) => !isStatus(status))) {
        disabledUTRs.push(question.universalTracker.getId());
      }
    })
  );
  return disabledUTRs;
}

export const disableByStandards = ({ surveyGroups, filterByStandard }: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByStandard'>) => {

  if (!filterByStandard || filterByStandard.length === 0) {
    return [];
  }

  const disabledUTRs: string[] = [];
  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      filterByStandard.forEach((standardCode) => {
        if (!question.universalTracker.hasAlternativeInfo(standardCode)) {
          disabledUTRs.push(question.universalTracker.getId());
        }
      });
    })
  );
  return disabledUTRs;
}

export const disableByRegulatory = ({ surveyGroups, filterByRegulatory }: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByRegulatory'>) => {

  if (!filterByRegulatory || filterByRegulatory.length === 0) {
    return [];
  }

  const disabledUTRs: string[] = [];
  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      filterByRegulatory.forEach((code) => {
        if (!question.universalTracker.hasAlternativeInfo(code) && !question.universalTracker.hasSubTag(code)) {
          disabledUTRs.push(question.universalTracker.getId());
        }
      });
    })
  );
  return disabledUTRs;
}

export const disableByFrameworks = ({ surveyGroups, filterByFramework }: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByFramework'>) => {
  if (!filterByFramework || filterByFramework.length === 0) {
    return [];
  }

  const disabledUTRs: string[] = [];
  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      filterByFramework.forEach((frameworkSubCode) => {
        if (!question.universalTracker.hasSubTag(frameworkSubCode)) {
          disabledUTRs.push(question.universalTracker.getId());
        }
      });
    })
  );
  return disabledUTRs;
}

export const disableByMateriality = ({ surveyGroups, filterByMateriality, blueprint, materiality }: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByMateriality' | 'blueprint' | 'materiality'>) => {

  if (!filterByMateriality || filterByMateriality.length === 0) {
    return [];
  }
  const getMaterialityCode = (sdgCode: number) => {
    if (!materiality) {
      return Materiality.None;
    }
    if (materiality[sdgCode] > 66) {
      return Materiality.High;
    } else if (materiality[sdgCode] > 33) {
      return Materiality.Medium;
    } else if (materiality[sdgCode] > 0) {
      return Materiality.Low;
    }
    return Materiality.None;
  }

  const goalCodes: string[] = [];
  SDG_CODES.forEach((sdgCode) => {
    const materialityCode = getMaterialityCode(sdgCode);
    if (filterByMateriality.includes(materialityCode)) {
      goalCodes.push(`sdg/${sdgCode}`);
    }
  });

  const disabledUTRs: string[] = [];
  const hasNoneFilter = filterByMateriality.includes(Materiality.None);
  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      const utrCode = question.universalTracker.getCode();
      const utrMateriality = blueprint?.[utrCode];

      const hasMateriality =
        utrMateriality && goalCodes.length && goalCodes.some((code) => utrMateriality.includes(code));
      const hasNoneMateriality = !utrMateriality && hasNoneFilter;

      if (!(hasMateriality || hasNoneMateriality)) {
        disabledUTRs.push(question.universalTracker.getId());
      }
    })
  );
  return disabledUTRs;
}

export const disableByDelegationStatus = ({ surveyGroups, filterByDelegationStatus, user }: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByDelegationStatus' | 'user'>) => {

  if (!filterByDelegationStatus || filterByDelegationStatus.length === 0) {
    return [];
  }
  const currentUserId = user?._id;

  const getDelegationStatusByCode = (question: ScopeQuestionOptionalValue, code: string) => {
    switch (code) {
      case 'user-a':
        return currentUserId && question.utrv?.stakeholders && (
          question.utrv.stakeholders.stakeholder.includes(currentUserId) || question.utrv.stakeholders.verifier.includes(currentUserId)
        );
      case 'con-u':
        return !question.utrv?.stakeholders || question.utrv.stakeholders.stakeholder.length === 0;
      case 'con-a':
        return question.utrv?.stakeholders && question.utrv.stakeholders.stakeholder.length > 0;
      case 'ver-u':
        return !question.utrv?.stakeholders || question.utrv.stakeholders.verifier.length === 0;
      case 'ver-a':
        return question.utrv?.stakeholders && question.utrv.stakeholders.verifier.length > 0;
      case 'mon-u':
      case 'mon-a':
      default:
        // Falls back to checking if the code is actually a user._id
        return question.utrv && (
          question.utrv.stakeholders.stakeholder.includes(code) || question.utrv.stakeholders.verifier.includes(code)
        );
    }
  }
  const disabledUTRs: string[] = [];
  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      filterByDelegationStatus.forEach((filterDelegationStatus: string) => {
        let disabled = !getDelegationStatusByCode(question, filterDelegationStatus);
        if (disabled) {
          disabledUTRs.push(question.universalTracker.getId());
        }
      });
    })
  );
  return disabledUTRs;
}

export const getFilteredUtrIds = (filterByTag: string[], tags: Tag[]) => {
  const tagMap = new Map<Tag['_id'], Tag['universalTrackers']>();
  tags.forEach((tag) => tagMap.set(tag._id, tag.universalTrackers));

  return filterByTag.reduce((acc, curTagId, index) => {
    const utrIds = tagMap.get(curTagId) || [];
    if (index === 0) {
      return utrIds;
    }
    return acc.filter((utrId) => utrIds.includes(utrId));
  }, [] as string[]);
};

export const getTaggedUtrIds = (tags: Tag[]) => {
  const tagSet = new Set<string>();
  tags.forEach(tag => {
    tag.universalTrackers?.forEach(utrId => {
      tagSet.add(utrId);
    });
  });

  return Array.from(tagSet);
}

export const disableByTag = ({
  surveyGroups,
  filterByTag,
  tags,
}: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByTag' | 'tags'>) => {
  if (!filterByTag || filterByTag.length === 0 || !tags) {
    return [];
  }
  const hasBothNoTagAndOthers = filterByTag.includes(TagStatus.NoTag) && filterByTag.length > 1;
  const hasNoTagOnly = filterByTag.includes(TagStatus.NoTag) && filterByTag.length === 1;
  const hasOtherTagsOnly = !filterByTag.includes(TagStatus.NoTag);

  const filteredUtrIds = getFilteredUtrIds(filterByTag, tags);
  const taggedUtrIds = getTaggedUtrIds(tags);

  const disabledUTRs: string[] = [];
  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      const noTagFilterOnly = hasNoTagOnly && taggedUtrIds.includes(question.universalTracker.getId());
      const otherTagsFilterOnly = hasOtherTagsOnly && !filteredUtrIds.includes(question.universalTracker.getId());

      if (hasBothNoTagAndOthers || noTagFilterOnly || otherTagsFilterOnly) {
        disabledUTRs.push(question.universalTracker.getId());
      }
    })
  );
  return disabledUTRs;
};

export const disableBySearch = ({ surveyGroups, searchText, searchIndex }: Pick<DisableUtrsProps, 'surveyGroups' | 'searchText' | 'searchIndex'>) => {

  if (!searchText || !searchIndex) {
    return [];
  }

  const result = searchIndex.search(searchText);
  const matchedIds = new Set(result.map((question) => question.result).flat())


  const disabledUTRs: string[] = [];
  surveyGroups.forEach((questionGroup) => {
    questionGroup.list.forEach((question) => {
      if (!matchedIds.has(question.universalTracker.getId())) {
        disabledUTRs.push(question.universalTracker.getId());
      }
    })
  });

  return disabledUTRs;
}

export const disableByBookmarks = ({
  surveyGroups,
  filterByBookmarks,
  bookmarks,
}: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByBookmarks' | 'bookmarks'>) => {
  if (!filterByBookmarks || !bookmarks?.length) {
    return [];
  }

  const disabledUTRs: string[] = [];

  surveyGroups.forEach((questionGroup) => {
    questionGroup.list.forEach((question) => {
      if (question.utrv?._id && !bookmarks.includes(question.utrv._id)) {
        disabledUTRs.push(question.universalTracker.getId());
      }
    });
  });

  return disabledUTRs;
};

export const hasUTRVFilter = ({
  filterByGoal,
  filterByStatus,
  filterByStandard,
  filterByFramework,
  filterByRegulatory,
  filterByMateriality,
  filterByDelegationStatus,
  filterByTag,
  searchText,
  filterByBookmarks,
}: Partial<SurveySettings>) => {
  if (filterByGoal && filterByGoal.length > 0) {
    return true;
  }
  if (filterByStatus && filterByStatus.length > 0) {
    return true;
  }
  if (filterByStandard && filterByStandard.length > 0) {
    return true;
  }
  if (filterByFramework && filterByFramework.length > 0) {
    return true;
  }
  if (filterByRegulatory && filterByRegulatory.length > 0) {
    return true;
  }
  if (filterByMateriality && filterByMateriality.length > 0) {
    return true;
  }
  if (filterByDelegationStatus && filterByDelegationStatus.length > 0) {
    return true;
  }
  if (filterByTag && filterByTag.length > 0) {
    return true;
  }
  if (searchText && searchText.length > 0) {
    return true;
  }
  if (filterByBookmarks) {
    return true;
  }
  return false;
};

export const generateDisabledUtrs = (props: DisableUtrsProps) => [
  ...disableByGoal(props),
  ...disableByStatus(props),
  ...disableByStandards(props),
  ...disableByFrameworks(props),
  ...disableByRegulatory(props),
  ...disableByMateriality(props),
  ...disableByDelegationStatus(props),
  ...disableByTag(props),
  ...disableBySearch(props),
  ...disableByBookmarks(props),
];
